import { Link } from "@tanstack/react-router";
import { BriefcaseBusinessIcon, CheckCheckIcon, CheckIcon, FileTextIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { type Decision, DecideDialog } from "@/approvals/DecideDialog";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatusPill, statusLabel } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/primitives";
import { formatDateValue, formatMoney } from "@/entities/format";
import { ago, useNotifications } from "@/notifications/api";
import { usePortalInvoices, usePortalProjects } from "./api";

const day = (v: string | null | undefined) => formatDateValue(v) || "—";

function Loading({ isError, what }: { isError: boolean; what: string }) {
  return isError ? (
    <p className="text-sm text-muted-foreground">Couldn't load your {what}. Try again shortly.</p>
  ) : (
    <Skeleton className="h-40 w-full" />
  );
}

/** Portal → Approvals: tickets waiting for this person's decision (their to-dos). */
export function PortalApprovalsPage() {
  const pane = useNotifications();
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const todos = (pane.data?.todos ?? []).filter((t) => t.entity === "ticket");
  return (
    <>
      <PageHeader title="Approvals" description="Tickets your colleagues raised that need your approval before work starts." />
      {!pane.data ? (
        <Loading isError={pane.isError} what="approvals" />
      ) : !todos.length ? (
        <EmptyState icon={CheckCheckIcon} title="Nothing to approve" description="Tickets that need your approval appear here." />
      ) : (
        <ul className="divide-y rounded-lg border bg-card" aria-label="Waiting for your approval">
          {todos.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="min-w-0 flex-1">
                <Link
                  to="/portal/$section"
                  params={{ section: "tickets" }}
                  search={{ ticket: t.entity_id }}
                  className="block font-medium break-words hover:underline"
                >
                  {t.title}
                </Link>
                <span className="block text-xs text-muted-foreground">
                  {[t.requested_by && `requested by ${t.requested_by}`, ago(t.created_at)].filter(Boolean).join(" · ")}
                </span>
              </span>
              <Button size="sm" onClick={() => setDeciding({ id: t.id, title: t.title, requestedBy: t.requested_by, decision: "approve" })}>
                <CheckIcon /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeciding({ id: t.id, title: t.title, requestedBy: t.requested_by, decision: "reject" })}>
                <XIcon /> Reject
              </Button>
            </li>
          ))}
        </ul>
      )}
      {deciding && <DecideDialog decision={deciding} onClose={() => setDeciding(null)} />}
    </>
  );
}

/** Portal → Projects: name, status and dates only. */
export function PortalProjectsPage() {
  const projects = usePortalProjects();
  return (
    <>
      <PageHeader title="Projects" description="Your projects with Datanomy." />
      {!projects.data ? (
        <Loading isError={projects.isError} what="projects" />
      ) : !projects.data.length ? (
        <EmptyState icon={BriefcaseBusinessIcon} title="No projects" description="Projects we're delivering for you appear here." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b">
                <th className="px-4 py-2 font-medium">Project</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Start</th>
                <th className="px-4 py-2 font-medium">End</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {projects.data.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium">{p.name ?? p.ref}</span>
                    {p.name && <span className="ml-2 font-mono text-xs text-muted-foreground">{p.ref}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusPill tone={p.status === "completed" ? "success" : p.status === "cancelled" ? "neutral" : "info"}>{statusLabel(p.status)}</StatusPill>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{day(p.start_date)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{day(p.end_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Portal → Invoices (needs the financial privilege; the server enforces it too). */
export function PortalInvoicesPage() {
  const invoices = usePortalInvoices();
  const outstanding = (invoices.data ?? []).filter((i) => i.outstanding > 0.005);
  return (
    <>
      <PageHeader title="Invoices" description="Your invoices and what's still outstanding." />
      {!invoices.data ? (
        <Loading isError={invoices.isError} what="invoices" />
      ) : !invoices.data.length ? (
        <EmptyState icon={FileTextIcon} title="No invoices" description="Invoices we've issued to you appear here." />
      ) : (
        <>
          {outstanding.length > 0 && (
            <p className="mb-3 text-sm text-muted-foreground">
              {outstanding.length} invoice{outstanding.length === 1 ? "" : "s"} outstanding.
            </p>
          )}
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr className="border-b">
                  <th className="px-4 py-2 font-medium">Invoice</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Due</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 text-right font-medium">Outstanding</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.data.map((i) => (
                  <tr key={i.id}>
                    <td className="px-4 py-2.5 font-mono">{i.number}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{day(i.date)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{day(i.due_date)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatMoney(i.gross_amount, i.currency)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {i.outstanding > 0.005 ? formatMoney(i.outstanding, i.currency) : <StatusPill tone="success">Paid</StatusPill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
